import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import Popup from 'reactjs-popup'
import { useMutation, useQueryClient } from 'react-query'
import { createExternalInstaller, deleteExternalInstaller, updateExternalInstaller } from '../../api/external_installers'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'

type Installer = {
  id: number
  name: string
  email: string
}

type ExternalInstallerPopupProps = {
  open: boolean
  mode: 'create' | 'edit'
  departmentId: number | null
  departmentTitle?: string
  installer?: Installer | null
  onClose: () => void
}

export default function ExternalInstallerPopup({
  open,
  mode,
  departmentId,
  departmentTitle,
  installer,
  onClose,
}: ExternalInstallerPopupProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  const onMutationSuccess = () => {
    queryClient.invalidateQueries('resources_overview')
    onClose()
  }

  const createMutation = useMutation(createExternalInstaller, { onSuccess: onMutationSuccess })
  const updateMutation = useMutation(({ id, payload }: { id: number; payload: any }) => updateExternalInstaller(id, payload), { onSuccess: onMutationSuccess })
  const deleteMutation = useMutation(({ id, payload }: { id: number; payload: any }) => deleteExternalInstaller(id, payload), { onSuccess: onMutationSuccess })

  useEffect(() => {
    if (mode === 'edit' && installer) {
      setName(installer.name || '')
      setEmail(installer.email || '')
      return
    }

    setName('')
    setEmail('')
  }, [mode, installer, open])

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isLoading = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading
  const canSubmit = Boolean(name.trim() && email.trim() && departmentId)

  const title = useMemo(() => {
    if (mode === 'edit') return `Rediger montør${departmentTitle ? ` - ${departmentTitle}` : ''}`
    return `Tilføj montør${departmentTitle ? ` - ${departmentTitle}` : ''}`
  }, [mode, departmentTitle])

  const closePopup = () => {
    createMutation.reset()
    updateMutation.reset()
    deleteMutation.reset()
    onClose()
  }

  const onSave = () => {
    if (!departmentId || !canSubmit) return

    const payload = {
      department_id: departmentId,
      name: name.trim(),
      email: email.trim(),
    }

    if (mode === 'create') {
      createMutation.mutate(payload)
      return
    }

    if (!installer) return
    updateMutation.mutate({ id: installer.id, payload })
  }

  const onDelete = () => {
    if (!departmentId || !installer) return
    if (!window.confirm('Er du sikker på at du vil slette montøren?')) return

    deleteMutation.mutate({ id: installer.id, payload: { department_id: departmentId } })
  }

  return (
    <Popup
      open={open}
      onClose={closePopup}
      position="center center"
      contentStyle={{ width: 'auto', maxWidth: '100%', minWidth: '420px', zIndex: 9999 }}
      overlayStyle={{ zIndex: 9998 }}
      modal
      nested
      closeOnDocumentClick={false}
    >
      <div className="modal-popup">
        <a className="close" onClick={closePopup}>
          &times;
        </a>
        <div className="popup-header">
          <h6 className="modal-title">{title}</h6>
        </div>
        <div className="popup-content">
          <div className="form-group">
            <label htmlFor="external-installer-name">Navn</label>
            <input
              id="external-installer-name"
              type="text"
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="external-installer-email">Email</label>
            <input
              id="external-installer-email"
              type="email"
              className="form-control"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <ErrorMessage isError={Boolean(error)} error={error} className="error-message mb-2" />
        </div>
        <div className="popup-actions d-flex justify-content-between">
          <div>
            {mode === 'edit' && (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={onDelete} disabled={isLoading}>
                Slet
              </button>
            )}
          </div>
          <div>
            <button type="button" className="btn btn-outline-secondary btn-sm mr-2" onClick={closePopup} disabled={isLoading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={!canSubmit || isLoading}>
              Save
            </button>
          </div>
        </div>
      </div>
    </Popup>
  )
}
